import { cn } from '@/lib/cn'

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** Section heading. Rendered in a flush title row above a hairline rule. */
  title?: React.ReactNode
  /** Trailing slot in the title row — a link, a badge, a count. */
  action?: React.ReactNode
  /** Tightens body padding for list-style content that supplies its own insets. */
  dense?: boolean
  contentClassName?: string
  /** Renders as this element instead of `section`. */
  as?: 'section' | 'div' | 'aside' | 'nav'
  children: React.ReactNode
}

/**
 * Hairline section container.
 *
 * Unlike {@link Card} this is a *structural* wrapper: one border, one soft
 * shadow, no inner surfaces. Panels never nest — a group that needs its own
 * frame inside a panel is a sign the section is doing two jobs.
 */
export const Panel = ({
  title,
  action,
  dense = false,
  className,
  contentClassName,
  as: Tag = 'section',
  children,
  ...rest
}: PanelProps): React.ReactElement => {
  return (
    <Tag
      className={cn(
        'rounded-[var(--radius-panel)] border border-gray-200 bg-white shadow-[var(--shadow-panel)]',
        'dark:border-gray-700/80 dark:bg-gray-800 dark:shadow-none',
        className
      )}
      {...rest}
    >
      {title != null && (
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-gray-700/70">
          {typeof title === 'string' ? (
            <h3 className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100">
              {title}
            </h3>
          ) : (
            title
          )}
          {action != null && <div className="shrink-0">{action}</div>}
        </div>
      )}
      <div className={cn(dense ? 'p-0' : 'p-5', contentClassName)}>{children}</div>
    </Tag>
  )
}

Panel.displayName = 'Panel'
